import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Share2, MessageCircle, ExternalLink, Book, Clock, Award, Check } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import toast from 'react-hot-toast';
import ReviewCard from '../Reviews/ReviewCard';
import RelatedProducts from './RelatedProducts';

interface FlashcardDetailProps {
  id?: string;
}

const flashcardData = {
  'terminale-notions': {
    id: 'terminale-notions',
    level: 'terminale',
    title: 'Flashcards Notions Terminale',
    price: 14.99,
    description: 'Toutes les définitions et concepts clés du programme de Terminale',
    image: 'https://images.unsplash.com/photo-1516979187457-637abb4f9353?ixlib=rb-4.0.3',
    tag: 'Bestseller',
    chapters: {
      economie: [
        'Croissance économique',
        'Commerce international',
        'Chômage',
        'Europe'
      ],
      sociologie: [
        'Structure sociale',
        'Mobilité sociale',
        'Travail-Emploi',
        'Engagement politique'
      ],
      regard_croise: [
        'Environnement'
      ]
    },
    features: [
      'Plus de 100 cartes de définitions',
      'Codes couleur par chapitre',
      'Format pratique et transportable',
      'Cartes plastifiées résistantes',
      'Boîte de rangement incluse'
    ],
    highlights: [
      {
        icon: Book,
        title: '100+ Cartes',
        description: 'Couvrant tout le programme'
      },
      {
        icon: Clock,
        title: 'Gain de temps',
        description: 'Révisions efficaces et rapides'
      },
      {
        icon: Award,
        title: 'Qualité Premium',
        description: 'Cartes plastifiées durables'
      }
    ],
    reviews: [
      {
        name: 'Emma L.',
        role: 'Élève de Terminale',
        content: 'Ces flashcards sont un véritable gain de temps pour les révisions. Le code couleur permet de s\'y retrouver facilement entre les différents chapitres.',
        rating: 5,
        date: '15 mars 2024'
      },
      {
        name: 'Thomas M.',
        role: 'Professeur de SES',
        content: 'Un excellent outil pédagogique qui permet aux élèves de réviser efficacement. La structure est claire et le contenu parfaitement adapté au programme.',
        rating: 5,
        date: '12 mars 2024'
      },
      {
        name: 'Julie D.',
        role: 'Élève de Terminale',
        content: 'J\'utilise ces cartes tous les jours dans le bus. C\'est super pratique et les explications sont très claires.',
        rating: 5,
        date: '8 mars 2024'
      }
    ]
  }
  // ... rest of the flashcardData object
};

const FlashcardDetail: React.FC<FlashcardDetailProps> = ({ id }) => {
  const navigate = useNavigate();
  const { dispatch } = useCart();
  const product = id ? flashcardData[id as keyof typeof flashcardData] : null;

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h2 className="text-2xl font-bold text-[#323c5f] mb-4">Produit non trouvé</h2>
        <button
          onClick={() => navigate('/flashcards')}
          className="inline-flex items-center text-[#9a6ebd] hover:text-[#8a5dad]"
        >
          <ArrowLeft className="mr-2" size={20} />
          Retour aux flashcards
        </button>
      </div>
    );
  }

  // Schéma JSON-LD pour le produit
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.title,
    "image": product.image,
    "description": product.description,
    "brand": {
      "@type": "Brand",
      "name": "Comprendre les SES"
    },
    "offers": {
      "@type": "Offer",
      "price": product.price.toFixed(2),
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock",
      "url": `https://cours-ses.fr/flashcards/${product.id}`,
      "priceValidUntil": "2025-12-31",
      "hasMerchantReturnPolicy": {
        "@type": "MerchantReturnPolicy",
        "applicableCountry": "FR",
        "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
        "merchantReturnDays": 14,
        "returnMethod": "https://schema.org/ReturnByMail",
        "returnFees": "https://schema.org/FreeReturn"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": "3.99",
          "currency": "EUR"
        },
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": "FR"
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": "1",
            "maxValue": "2",
            "unitCode": "DAY"
          },
          "transitTime": {
            "@type": "QuantitativeValue",
            "minValue": "1",
            "maxValue": "3",
            "unitCode": "DAY"
          }
        }
      }
    },
    "review": product.reviews.map(review => ({
      "@type": "Review",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": review.rating.toString(),
        "bestRating": "5"
      },
      "author": {
        "@type": "Person",
        "name": review.name
      },
      "reviewBody": review.content
    })),
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "500",
      "bestRating": "5",
      "worstRating": "1"
    }
  };

  const handleAddToCart = () => {
    dispatch({
      type: 'ADD_ITEM',
      payload: {
        id: product.id,
        title: product.title,
        price: product.price,
        quantity: 1
      }
    });
    toast.success('Produit ajouté au panier !');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <button
        onClick={() => navigate('/flashcards')}
        className="flex items-center text-[#9a6ebd] hover:text-[#8a5dad] mb-8"
      >
        <ArrowLeft className="mr-2" size={20} />
        Retour aux flashcards
      </button>

      <div className="grid md:grid-cols-2 gap-12 mb-16">
        <div>
          <img
            src={product.image}
            alt={product.title}
            className="w-full rounded-lg shadow-lg mb-8"
          />
          
          <div className="grid grid-cols-3 gap-4 mb-8">
            {product.highlights.map((highlight, index) => (
              <div key={index} className="bg-white p-4 rounded-lg shadow-md text-center">
                <highlight.icon className="mx-auto text-[#9a6ebd] mb-2" size={24} />
                <h4 className="font-semibold text-[#323c5f] mb-1">{highlight.title}</h4>
                <p className="text-sm text-gray-600">{highlight.description}</p>
              </div>
            ))}
          </div>

          <div className="bg-[#ddcbeb] bg-opacity-20 p-6 rounded-lg mb-8">
            <h3 className="text-lg font-semibold text-[#323c5f] mb-4">
              Caractéristiques
            </h3>
            <ul className="space-y-2">
              {product.features.map((feature, index) => (
                <li key={index} className="flex items-center text-gray-600">
                  <Check className="text-[#9a6ebd] mr-2" size={16} />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div>
          <div className="sticky top-24">
            {product.tag && (
              <span className="inline-block bg-[#9a6ebd] text-white px-3 py-1 rounded-full text-sm font-medium mb-4">
                {product.tag}
              </span>
            )}
            <h1 className="text-3xl font-bold text-[#323c5f] mb-4">
              {product.title}
            </h1>
            <p className="text-xl text-gray-600 mb-6">
              {product.description}
            </p>
            <div className="text-3xl font-bold text-[#323c5f] mb-8">
              {product.price.toFixed(2)}€
            </div>

            <button
              onClick={handleAddToCart}
              className="w-full bg-[#9a6ebd] text-white px-6 py-4 rounded-lg hover:bg-[#8a5dad] transition-colors flex items-center justify-center text-lg font-semibold mb-8"
            >
              Ajouter au panier
            </button>

            <div>
              <h2 className="text-xl font-semibold text-[#323c5f] mb-4">
                Chapitres couverts
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="font-medium text-[#323c5f] mb-2">Économie</h3>
                  <ul className="space-y-2">
                    {product.chapters.economie.map((chapter, index) => (
                      <li key={index} className="flex items-center text-gray-600">
                        <span className="text-[#9a6ebd] mr-2">•</span>
                        {chapter}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="font-medium text-[#323c5f] mb-2">Sociologie et Science Politique</h3>
                  <ul className="space-y-2">
                    {product.chapters.sociologie.map((chapter, index) => (
                      <li key={index} className="flex items-center text-gray-600">
                        <span className="text-[#9a6ebd] mr-2">•</span>
                        {chapter}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="font-medium text-[#323c5f] mb-2">Regards Croisés</h3>
                  <ul className="space-y-2">
                    {product.chapters.regard_croise.map((chapter, index) => (
                      <li key={index} className="flex items-center text-gray-600">
                        <span className="text-[#9a6ebd] mr-2">•</span>
                        {chapter}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <RelatedProducts currentProductId={product.id} level={product.level} />
    </div>
  );
};

export default FlashcardDetail;